import {
  ComputedDataKey,
  LIVE_MAP_DEBUG_COURSE_CODES,
  LIVE_MAP_DEBUG_COURSE_WITH_CODE_EVERY_N,
  RideComputedDataKeys,
} from "../../constants";
import { IRideDto } from "../../dto";
import { isCourseLineColorUsable } from "./live-map.helpers";

const COURSE_CODE_MARKER_LENGTH = 3;

const trimCourseCode = (
  value: string | undefined | null,
): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

/**
 * Course client code from ride DTO (`courseCode` on ride).
 * Backend sets `metadata.clientCode` only (no `routeNumber` fallback).
 */
export const getRideCourseCode = (
  ride: Pick<IRideDto, "courseId" | "courseCode">,
): string | undefined => {
  if (!ride.courseId) {
    return undefined;
  }
  return trimCourseCode(ride.courseCode);
};

const isDebugCourseMarkerRideIndex = (rideId: number): boolean =>
  rideId % LIVE_MAP_DEBUG_COURSE_WITH_CODE_EVERY_N === 0;

/** Every even ride id in diagnostics/debug: preview course pin when API has no courseId. */
export const isDebugCourseTrackingMarkerPreview = (
  rideId: number | undefined,
  debugMode?: boolean,
): boolean =>
  Boolean(
    debugMode && rideId !== undefined && isDebugCourseMarkerRideIndex(rideId),
  );

/** Ride is a fixed course on live map (including debug preview rides). */
export const shouldUseCourseTrackingMarker = ({
  courseId,
  rideId,
  debugMode,
}: {
  courseId?: number | null;
  rideId?: number;
  debugMode?: boolean;
}): boolean =>
  Boolean(courseId) || isDebugCourseTrackingMarkerPreview(rideId, debugMode);

/**
 * Client code for tracking marker / pin label (API value only when courseId is set;
 * synthetic code on even ride ids in debug when courseId is absent).
 */
export const resolveTrackingCourseCode = ({
  courseId,
  courseCode,
  rideId,
  debugMode,
}: {
  courseId?: number | null;
  courseCode?: string;
  rideId?: number;
  debugMode?: boolean;
}): string | undefined => {
  if (courseId) {
    return getRideCourseCode({ courseId, courseCode });
  }
  if (
    !debugMode ||
    rideId === undefined ||
    !isDebugCourseMarkerRideIndex(rideId)
  ) {
    return undefined;
  }
  return LIVE_MAP_DEBUG_COURSE_CODES[
    rideId % LIVE_MAP_DEBUG_COURSE_CODES.length
  ];
};

/** Course pin balloon only when a client code is available (first 3 chars shown). */
export const hasCourseClientCodeForTrackingMarker = (params: {
  courseId?: number | null;
  courseCode?: string;
  rideId?: number;
  debugMode?: boolean;
}): boolean => Boolean(resolveTrackingCourseCode(params)?.trim());

/** First three characters of course client code for the live tracking map marker. */
export const getCourseCodeMarkerLabel = (courseCode: string): string => {
  const trimmed = courseCode.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.slice(0, COURSE_CODE_MARKER_LENGTH);
};

/** Course row in vehicle tracking popup (fixed course rides only, not geo). */
export const shouldShowCourseInTrackingPopover = ({
  courseId,
}: {
  courseId?: number | null;
}): boolean => Boolean(courseId);

export const getTrackingMarkerBorderColor = ({
  courseLineColor,
  lineColor,
}: {
  courseLineColor: string | undefined;
  lineColor: string;
}): string => {
  const trimmedCourse = courseLineColor?.trim() ?? "";
  if (trimmedCourse && isCourseLineColorUsable(trimmedCourse)) {
    return trimmedCourse;
  }
  return lineColor;
};

export const getRideCourseLineColorFromDto = (
  ride: IRideDto,
): string | undefined => {
  const raw = ride[ComputedDataKey]?.[RideComputedDataKeys.courseLineColor];
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return trimmed && isCourseLineColorUsable(trimmed) ? trimmed : undefined;
};
